/* import __COLOCATED_TEMPLATE__ from './predicates-editor-side-sheet.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
//@ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';
//@ts-ignore no type declaration available for predicates
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { ROOT_PARENT_SELECTOR } from '@intercom/pulse/lib/popover-utils';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    onClose: () => void;
    isOpen: boolean;
    routingOutcomeId?: string;
    predicateGroup?: $TSFixMe;
    // NEW UI props (Feature Flag ON: sdr-config-setup-v2)
    conditionId?: string;
    routeTitle?: string;
    conditionTitle?: string;
    mode?: 'add' | 'edit';
  };
}

export default class PredicatesEditorSideSheet extends Component<Signature> {
  @service declare attributeService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare appService: $TSFixMe;

  // NEW UI: Element for blanket overlay
  rootParentElement = document.querySelector(ROOT_PARENT_SELECTOR);

  // Check if using NEW UI (Feature Flag ON: sdr-config-setup-v2)
  get useNewUI(): boolean {
    return this.appService.app.canUseFeature('sdr-config-setup-v2');
  }

  get attributeGroupList() {
    let canUseFinAttributes = this.appService.app.canUseFeature('fin-sdr-attribute-categorisation');

    // Always separate classification attributes from regular ones
    let regularAttributes = this.attributeService.botAutoMessageCollectableAttributes.filter(
      (attr: any) => !attr.isClassificationAttribute,
    );
    let regularGroups = this.attributeService.attributesToGroupList(regularAttributes);

    // Build Fin Attributes group at the bottom when FF is enabled
    let finGroup: { heading: string; attributes: any[] } | null = null;
    if (canUseFinAttributes) {
      let classificationAttributes =
        this.attributeService.botAutoMessageCollectableAttributes.filter(
          (attr: any) => attr.isClassificationAttribute,
        );
      if (classificationAttributes.length > 0) {
        finGroup = {
          heading: this.intl.t('ai-agent.sales-development.predicates-editor-side-sheet.fin-data'),
          attributes: classificationAttributes,
        };
      }
    }

    // NEW UI: Don't include natural language condition (uses Fin's judgment text area instead)
    if (this.useNewUI) {
      return finGroup ? [...regularGroups, finGroup] : regularGroups;
    }

    // OLD UI: Include natural language condition in dropdown
    let naturalLanguageAttribute = {
      identifier: 'llm_evaluation',
      name: this.intl.t(
        'ai-agent.sales-development.predicates-editor-side-sheet.natural-language-condition',
      ),
      type: 'llm_evaluation',
      icon: null,
    };

    let naturalLanguageGroup = {
      heading: null,
      attributes: [naturalLanguageAttribute],
    };

    let groups = [naturalLanguageGroup, ...regularGroups];
    return finGroup ? [...groups, finGroup] : groups;
  }

  // NEW UI: Breadcrumb title for header
  get breadcrumbTitle() {
    let mode = this.args.mode || 'edit';
    if (mode === 'add') {
      return this.intl.t(
        'ai-agent.sales-development.predicates-editor-side-sheet.add-hard-criteria',
      );
    } else {
      return this.intl.t(
        'ai-agent.sales-development.predicates-editor-side-sheet.edit-hard-criteria',
      );
    }
  }

  get predicateContext() {
    return {
      mode: 'fin-sdr',
    };
  }

  @action
  onPredicatesUpdate(predicates: any[]) {
    if (!this.args.predicateGroup) {
      return;
    }

    this.args.predicateGroup.set('predicates', copy(predicates));
  }

  @action
  savePredicates() {
    if (!this.args.predicateGroup) {
      return;
    }

    let predicates = this.args.predicateGroup.predicates.serialize() || [];

    this.reactTeammateAppIsland.dispatchTeammateAppEvent('fin-sdr:predicates:updated', {
      routingOutcomeId: this.args.routingOutcomeId,
      // NEW UI: Include conditionId when provided (for condition-level predicates)
      conditionId: this.args.conditionId,
      predicates,
    });

    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::SalesDevelopment::PredicatesEditorSideSheet': typeof PredicatesEditorSideSheet;
    'ai-agent/sales-development/predicates-editor-side-sheet': typeof PredicatesEditorSideSheet;
  }
}
